import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { createSelector } from '@ngrx/store';
import { absenceOverlapsPeriod } from '@reducers/orm/absence/absence.helper';
import { checkAbsenteePermission } from '@reducers/orm/absence/absence.permission.helper';
import { getAbsenceEnhanced } from '@reducers/orm/absence/absence.service';
import { getDepartmentEntities } from '@reducers/orm/department/department.service';
import {
  employeeHasTeamInDepartments,
  getEmployeeTeamDepartmentsWithoutFlexpool,
} from '@reducers/orm/employee/employee.selectors';
import { getLocationEntities } from '@reducers/orm/location/location.service';
import { getTeamEntities } from '@reducers/orm/team/team.service';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';

import { type PayrollRunScope } from '../../payroll.model';
import { buildPayrollAbsenceRow, emptyPayrollAbsenceGroups, type PayrollAbsenceGroups } from './payroll-absence-row';

/**
 * Absences overlapping the payroll run's period, in its departments and visible to the manager,
 * grouped into the step's three sections.
 *
 * A factory rather than a plain selector because the period belongs to the run, not to global
 * page state — build it once per scope so memoization survives change detection.
 *
 * Deliberately NOT `getPendingAbsencesForSupervisor`, which is unusable here three times over: it
 * filters by `getSelectedDepartmentIds` (the app's global filter, not the run's scope — the same
 * leak `payroll-sources.ts` documents for the lock step), it drops absences failing the 'approve'
 * permission check, and it returns pending absences alone. This selector applies the sibling
 * 'view' check instead — same as `absenceForPeriod`/`absenceForDateRange` in `absence.service.ts`
 * — which decides whether a row is listed at all. 'approve' is a different question, read per row
 * for its action control only: a row the manager cannot approve is still outstanding work and
 * must still be listed, just with that control hidden.
 */
export const selectPayrollAbsenceGroups = (scope: PayrollRunScope) =>
  createSelector(
    getAbsenceEnhanced,
    getEmployeeTeamDepartmentsWithoutFlexpool,
    getTeamEntities,
    getDepartmentEntities,
    getLocationEntities,
    getPermissionState,
    (
      absences,
      employeeTeamDepartments,
      teamsById,
      departmentsById,
      locationsById,
      permissionState,
    ): PayrollAbsenceGroups => {
      const periodStart = toISODateString(scope.range.start);
      const periodEnd = toISODateString(scope.range.end);
      const overlapsPeriod = absenceOverlapsPeriod(periodStart, periodEnd);
      const groups = emptyPayrollAbsenceGroups();

      for (const absence of absences) {
        if (!overlapsPeriod(absence)) {
          continue;
        }

        if (!employeeHasTeamInDepartments(absence.user_id, employeeTeamDepartments, scope.departmentIds)) {
          continue;
        }

        if (!checkAbsenteePermission(absence, employeeTeamDepartments, permissionState, 'view')) {
          continue;
        }

        const row = buildPayrollAbsenceRow(absence, teamsById, departmentsById, locationsById);

        // An unrecognised status has no section to go in — skip it rather than crash on a
        // `groups[null]`.
        if (row.category) {
          groups[row.category].push(row);
        }
      }

      return groups;
    },
  );
